import { AxiosInstance } from 'axios';
import { ErrorInterpretationDto, ErrorRelationState } from './api';

/**
 * Following one object through the storage account of an environment.
 *
 * Written by hand rather than generated. The generated client in `api.ts` is overwritten
 * from the Swagger document on every regeneration, and everything added to it by hand
 * disappears with it - the same reason the queue monitor and the health panel keep their own
 * clients here.
 *
 * The shapes mirror the DTOs under `Controllers/Dto/Trace`. Enums arrive as strings: the API
 * serialises them with a string enum converter, so the unions below are what actually comes
 * over the wire.
 */

/** How an id was matched to an object. Mirrors TraceResolvedBy. */
export type TraceResolvedBy =
    | 'None'
    | 'Relations'
    | 'ObjectPayload'
    | 'ExactId'
    | 'InboundEvent'
    | 'LogMention';

/** One way of looking for an id. Mirrors TraceRoute. */
export type TraceRoute =
    | 'Relations'
    | 'ObjectPayload'
    | 'ExactId'
    | 'InboundEvent'
    | 'LogColumns'
    | 'LogText';

/** Why a search stopped where it did. Mirrors TraceSearchStop. */
export type TraceSearchStop =
    | 'Found'
    | 'RangeExhausted'
    | 'DayBudget'
    | 'Failed'
    | 'MentionsOnly';

/** Which table a timeline entry came from. Mirrors TraceSource. */
export type TraceSource = 'QueueMessageLog' | 'SyncLog' | 'Log' | 'Queue';

/** Where the day window came from. Mirrors TraceWindowSource. */
export type TraceWindowSource = 'SyncLog' | 'RequestedRange' | 'Evidence';

/** How a related object was linked. Mirrors TraceRelationVia. */
export type TraceRelationVia =
    | 'AdditionalInfo1'
    | 'AdditionalInfo2'
    | 'MessageText';

/** What tied a row to the object. Mirrors TraceMatch. */
export type TraceMatch =
    | 'QueueMessage'
    | 'AdditionalInfo1'
    | 'AdditionalInfo2'
    | 'MessageText'
    | 'SyncState'
    | 'InboundEvent';

/** What a claim about the owning app rests on. Mirrors TraceOwnerBasis. */
export type TraceOwnerBasis = 'Topology' | 'Observation';

/** How bad a row is. Mirrors LogMessageLevel. */
export type TraceLevel = 'Verbose' | 'Information' | 'Warning' | 'Error';

/** One object an id could be about. */
export interface TraceCandidate {
    objectType: string;
    relationId?: string | null;
    inExact: boolean;
    dependingId?: string | null;
    objectGuid?: string | null;
    payloadTable?: string | null;
    payloadExists?: boolean | null;
    relationLastModified?: string | null;
    foundBy: TraceResolvedBy;
}

/** What one route did, and what its answer means. Mirrors TraceRouteDto. */
export interface TraceRouteReport {
    route: TraceRoute;
    searched: boolean;
    hits: number;
    explanation?: string | null;
}

/**
 * Which routes ran, over which days, and what is left to look at. Mirrors TraceSearchDto.
 *
 * The half of the answer that does the work when there is nothing to show. `from` and `to`
 * are the days that were actually read - a search stops at its first hit, so that is rarely
 * the range that was asked for - and `nextFrom`/`nextTo` are what a "search further back"
 * would cover.
 */
export interface TraceSearch {
    from: string;
    to: string;
    daysSearched: number;
    /** The chunk the hits came out of, which is what the timeline is read over. */
    hitFrom?: string | null;
    hitTo?: string | null;
    found: boolean;
    stoppedBecause: TraceSearchStop;
    moreDaysAvailable: boolean;
    nextFrom?: string | null;
    nextTo?: string | null;
    routes: TraceRouteReport[];
}

export interface TraceCandidates {
    customer: string;
    environment: string;
    id?: string | null;
    resolvedBy: TraceResolvedBy;
    candidates: TraceCandidate[];
    /** The objects that named this id without being it. */
    mentions: TraceRelated[];
    search: TraceSearch;
    /** The raw webhook bodies that name this id, when the inbound tables were searched. */
    inbound?: TraceInbound | null;
    gaps: string[];
    error?: string | null;
}

/** One raw webhook body that names the id. Mirrors TraceInboundHit. */
export interface TraceInboundHit {
    table: string;
    /** The partition key, and the first half of the message that re-offers this body. */
    eventType: string;
    /** The row key, and the second half of that message. */
    eventId: string;
    at?: string | null;
    /**
     * Which part of `content` carries the id - `invoice`, `transaction` - or null when the
     * id only occurs somewhere in the text.
     */
    matchedAs?: string | null;
    /**
     * Whether this body is that object's own webhook rather than another object's webhook
     * that happens to carry it. A payment webhook ships the invoice it paid, so a hit on an
     * invoice number is regularly a payment - and reprocessing that would put the payment
     * through again while the invoice stayed where it was.
     */
    isSubject: boolean;
    objectTypes: string[];
}

/** What a search of the inbound tables looked at. Mirrors TraceInboundResult. */
export interface TraceInbound {
    from: string;
    to: string;
    tables: string[];
    /** The tables that could not be read, so their silence proves nothing. */
    unread: string[];
    scanned: number;
    truncated: boolean;
    hits: TraceInboundHit[];
}

export interface TraceWindow {
    source: TraceWindowSource;
    days: string[];
    from: string;
    to: string;
    /** The days the sync log itself named, before the margin around them was added. */
    syncLogDays: string[];
}

export interface TraceQuery {
    customer: string;
    environment: string;
    id?: string | null;
    objectType?: string | null;
    relationId?: string | null;
    from: string;
    to: string;
    resolvedBy: TraceResolvedBy;
    window: TraceWindow;
    stackTraces: boolean;
}

export interface TraceWaiting {
    queue: string;
    /** How far from the front it sits, counting from one. */
    position: number;
    dequeueCount: number;
    insertedOn?: string | null;
    depth: number;
    /**
     * The body of that message, decoded, exactly as it sits on the queue.
     *
     * The one raw message on the whole panel that was read rather than rebuilt. Everything
     * else the chain prints as a queue message is composed from the keys that are known now.
     */
    message?: string | null;
}

export interface TraceSubject {
    objectType: string;
    relationId?: string | null;
    inExact: boolean;
    dependingId?: string | null;
    relationLastModified?: string | null;
    objectGuid?: string | null;
    /** What the last message said had happened; completes the queue message. */
    eventType: string;
    payloadExists?: boolean | null;
    payloadTable?: string | null;
    /** When that copy was last written; the one dated moment in the first half of the road. */
    payloadAt?: string | null;
    /** When the send function was done with that copy; it is kept after that only to show what was sent. */
    payloadHandledAt?: string | null;
    /** How it was done with it: Succeeded, AlreadySynced or Unhandled. */
    payloadOutcome?: string | null;
    /**
     * Whether the raw webhook that carried this object is still waiting to be processed.
     *
     * Null means nothing looked, not "no". The inbound tables are keyed by the sending
     * system's event id, so they are only searched when no key route found the object.
     */
    inboundExists?: boolean | null;
    inboundTable?: string | null;
    inboundEventType?: string | null;
    inboundEventId?: string | null;
    inboundAt?: string | null;
    lastState?: string | null;
    failCount?: number | null;
    lastMessage?: string | null;
    waitingIn?: TraceWaiting | null;
    customerId?: string | null;
    invoiceId?: string | null;
    /** Where the object stands towards Exact, the way the error interpretation reads it. */
    exactState?: ErrorRelationState;
}

export interface TraceRelated {
    objectType: string;
    relationId: string;
    via: TraceRelationVia;
    inExact: boolean;
    dependingId?: string | null;
    lastSeenAt?: string | null;
    lastMessage?: string | null;
    attempts: number;
}

export interface TraceEntry {
    id: string;
    at?: string | null;
    source: TraceSource;
    day: string;
    attempt?: number | null;
    eventType: string;
    queueMessage?: string | null;
    level: TraceLevel;
    errorType?: string | null;
    message?: string | null;
    functionalMessage?: string | null;
    /** What a failed attempt means and who has to act; only on rows a rule recognises. */
    interpretation?: ErrorInterpretationDto | null;
    stackTrace?: string | null;
    hasStackTrace: boolean;
    state?: string | null;
    failCount?: number | null;
    customerId?: string | null;
    invoiceId?: string | null;
    matchedOn: TraceMatch;
    /**
     * The station of the journey this row was written at, or null when the table it came
     * from does not say. Null for the general log, whose rows are written from anywhere in
     * an app.
     */
    stationKey?: string | null;
    /** The stored copy this row was about, when the row named one. */
    objectGuid?: string | null;
    /** The stored bodies this row points at: what went in, as far as the tables can say. */
    data: TraceDataRef[];
}

export interface TraceOwner {
    queue?: string | null;
    function?: string | null;
    app?: string | null;
    appDisplayName?: string | null;
    lastHeartbeat?: string | null;
    verdict?: string | null;
    basis: TraceOwnerBasis;
    note?: string | null;
}

/** What can be done from here. Mirrors FindingActionDto, which the health panel shares. */
export interface TraceAction {
    kind: string;
    label: string;
    description: string;
    /**
     * What to watch for afterwards: how long it takes and how you tell whether it worked.
     *
     * The other half of a button that changes something. Without it the honest reading of a
     * press is "er is iets gebeurd, ergens", and the next click is a page refresh.
     */
    expectation?: string | null;
    queue?: string | null;
    mutates: boolean;
    /** Why pressing it would be premature right now, or null when it is safe to offer. */
    caveat?: string | null;
    /** Whether this is the one that follows from the verdict. */
    recommended: boolean;
    /**
     * Whether pressing it cannot achieve anything, in which case `caveat` is the reason.
     * Shown as ruled out rather than hidden: what is impossible is half the diagnosis.
     */
    blocked: boolean;
}

/** What sort of station a card is. Mirrors TraceStationKind. */
export type TraceStationKind =
    | 'System'
    | 'Http'
    | 'Queue'
    | 'Worker'
    | 'Store'
    | 'Poison';

/** What one object did at one station. Mirrors TraceStationState. */
export type TraceStationState =
    | 'Unknown'
    | 'Passed'
    | 'Here'
    | 'Failed'
    | 'Blocked'
    | 'Skipped';

/** One thing that is known at a station. Mirrors TraceFactDto. */
export interface TraceFact {
    label: string;
    value?: string | null;
    hint?: string | null;
    mono: boolean;
}

/** Which store a data pointer points into. Mirrors TraceDataKind. */
export type TraceDataKind = 'Payload' | 'InboundEvent';

/**
 * A pointer to one stored body. Mirrors TraceDataRefDto.
 *
 * A pointer rather than the body: a stored invoice runs to tens of kilobytes and one trace
 * can name a dozen of them, so the body is fetched when somebody opens it.
 */
export interface TraceDataRef {
    kind: TraceDataKind;
    label: string;
    hint?: string | null;
    objectType?: string | null;
    objectGuid?: string | null;
    eventType?: string | null;
    eventId?: string | null;
}

/** One stored body, as it came out of the table. Mirrors TraceDataDto. */
export interface TraceData {
    kind: TraceDataKind;
    label?: string | null;
    found: boolean;
    table?: string | null;
    partitionKey?: string | null;
    rowKey?: string | null;
    at?: string | null;
    length: number;
    contentType?: string | null;
    content?: string | null;
    truncated: boolean;
    error?: string | null;
}

/** One station on the road. Mirrors TraceStationDto. */
export interface TraceStation {
    key: string;
    kind: TraceStationKind;
    title: string;
    subtitle?: string | null;
    state: TraceStationState;
    /** When this station was the object's last known whereabouts, or null when nothing dates it. */
    at?: string | null;
    /** What that moment is the moment of, since it differs per station. */
    atHint?: string | null;
    summary?: string | null;
    facts: TraceFact[];
    queue?: string | null;
    entries: number;
    note?: string | null;
    /**
     * Something about the environment that bears on this step - an app with no sign of life -
     * kept apart from `note`, which qualifies what the step claims about the object.
     */
    warning?: string | null;
    /** Which of the offered actions belong at this step, by kind. */
    actionKinds: string[];
    /** The stored bodies that can be opened here. */
    data: TraceDataRef[];
    /** What arrives at this step: the message it reads, the body that message names. */
    input: TraceIo[];
    /** What it hands on, including the queue message the step below picks up. */
    output: TraceIo[];
    /**
     * The rows this step writes about this object, as rows. Mirrors TraceStationDto.Rows.
     *
     * Empty for every step that stores nothing, which is most of them. Where it is not
     * empty it is the answer to the question a summary cannot give: which table, under
     * which two keys, and is the row there right now.
     */
    rows?: TraceRow[] | null;
}

/** One row in one table, as it stands right now. Mirrors TraceRowDto. */
export interface TraceRow {
    table?: string | null;
    partitionKey?: string | null;
    rowKey?: string | null;
    /** Whether the row is there. Null when nothing looked, which is a third answer. */
    exists?: boolean | null;
    at?: string | null;
    hint?: string | null;
    columns: TraceCell[];
    /** The stored body in this row, when there is one to open. */
    data?: TraceDataRef | null;
}

/** One column of one row. Mirrors TraceCellDto. */
export interface TraceCell {
    /** Spelled as the entity spells it, so it can be found back in a storage browser. */
    name: string;
    value?: string | null;
    hint?: string | null;
    /** Whether it is a key, an id or a guid, and should be drawn as one. */
    mono: boolean;
}

/** What sort of thing travels into or out of a station. Mirrors TraceIoKind. */
export type TraceIoKind =
    | 'QueueMessage'
    | 'Body'
    | 'Row'
    | 'HttpCall'
    | 'ApiCall';

/**
 * One thing that goes into a station, or comes out of it. Mirrors TraceIoDto.
 *
 * The half of a step the panel used to leave out. A station said what state the object was
 * in and which rows were written there, and left the reader to work out what the step was
 * actually handed - which for every worker in this connector is one short comma-separated
 * string, and that string is what half the questions are about.
 */
export interface TraceIo {
    kind: TraceIoKind;
    /** What to call it: "Queuebericht", "Ruwe body". */
    label: string;
    /** Where it comes from or goes to: `queue chargebeein`, `tabel objectscb`. */
    where?: string | null;
    /** The thing itself, when it is short enough to print. */
    value?: string | null;
    /** What it is and what reads it. */
    hint?: string | null;
    /**
     * Whether `value` was read off a row, as opposed to composed from the keys that are known
     * now. A composed message is the right shape and not proof of what travelled.
     */
    observed: boolean;
    /** The stored body behind it, when there is one to open. */
    data?: TraceDataRef | null;
}

/**
 * The chain one object travels, with per station what is known about it there.
 *
 * The part of the answer a reader looks at first. `verdict` is the sentence they came for,
 * `nextStep` is the one after it, and `transferQueue` is where offering the object again
 * has to write - never a queue picked on screen, because the wrong one deletes the message
 * and records a success.
 */
export interface TraceJourney {
    pipeline: string;
    source: string;
    target: string;
    transferQueue?: string | null;
    /**
     * The queue the raw webhook is parked on, which is where offering a stored body again has
     * to write. The other queue: a message on `transferQueue` names a stored copy, one on this
     * queue names a raw body, and an object stuck in the first half of the chain has only the
     * second.
     */
    inboundQueue?: string | null;
    stations: TraceStation[];
    currentKey?: string | null;
    failedKey?: string | null;
    verdict?: string | null;
    tone: 'ok' | 'warn' | 'error' | 'unknown';
    nextStep?: string | null;
    /**
     * The paragraph between the one-sentence verdict and the evidence: what happened, why it
     * stopped, and what that means for the choice of button.
     */
    explanation?: string | null;
    /** The action that follows from the verdict, or 'None'. */
    recommendedAction: string;
}

/** Which step of a running trace this is. Mirrors TracePhase. */
export type TracePhase =
    | 'Resolve'
    | 'LogSearch'
    | 'Window'
    | 'Timeline'
    | 'Context'
    | 'Done';

/** What a running trace is doing. Mirrors TraceProgressDto. */
export interface TraceProgress {
    phase: TracePhase;
    label: string;
    detail?: string | null;
    done: number;
    total: number;
    finished: boolean;
    result?: string | null;
    elapsedMs: number;
}

/** One item on the trace stream. Mirrors TraceStreamMessage. */
export interface TraceStreamMessage {
    progress?: TraceProgress | null;
    trace?: ObjectTrace | null;
}

export interface ObjectTrace {
    query: TraceQuery;
    candidates: TraceCandidate[];
    search: TraceSearch;
    /** The raw webhook bodies naming this id, when the inbound tables were searched. */
    inbound?: TraceInbound | null;
    subject?: TraceSubject | null;
    /** Filled even without a subject: the objects that name the id you typed. */
    related: TraceRelated[];
    timeline: TraceEntry[];
    owner?: TraceOwner | null;
    journey?: TraceJourney | null;
    actions: TraceAction[];
    gaps: string[];
    error?: string | null;
}

/** What to ask for. Everything but the id is optional. */
export interface TraceRequest {
    id?: string;
    /** Pins the trace to one object, and reaches objects that have no relation row. */
    objectType?: string;
    relationId?: string;
    from?: Date;
    to?: Date;
    stackTraces?: boolean;
}

/** The axios instance is created with transformResponse: [], so the body is a string. */
const parse = <T>(data: unknown): T =>
    typeof data === 'string' ? (JSON.parse(data) as T) : (data as T);

/**
 * A day out of the API, as a local Date on that same calendar day.
 *
 * Built from the digits rather than handed to the Date constructor. The API writes a day as
 * `2026-08-14T00:00:00`, and a bare `2026-08-14` would be read as UTC midnight - which is
 * the day before in every timezone west of Greenwich, and sending that back as the start of
 * a search would silently shift the window.
 */
export const parseDay = (iso?: string | null): Date | null => {
    if (!iso || iso.length < 10) {
        return null;
    }

    const year = Number(iso.slice(0, 4));
    const month = Number(iso.slice(5, 7));
    const dayOfMonth = Number(iso.slice(8, 10));

    if (!year || !month || !dayOfMonth) {
        return null;
    }

    return new Date(year, month - 1, dayOfMonth);
};

/**
 * A date the way the API reads it: a day, without a time that means nothing here.
 *
 * Exported because the hub needs the same string. Handing a `Date` to the SignalR client
 * would serialise it through `toJSON`, which is UTC - and for anybody west of Greenwich
 * that silently moves the start of a search back a day.
 */
export const day = (date: Date): string =>
    `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(
        2,
        '0'
    )}-${`${date.getDate()}`.padStart(2, '0')}`;

/** Which objects one typed id could be about. */
export const resolveTrace = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    id: string
): Promise<TraceCandidates> => {
    const response = await axios.get(
        `/Trace/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}/resolve?id=${encodeURIComponent(id)}`
    );

    return parse<TraceCandidates>(response.data);
};

/** Everything the tables hold about one object. */
export const getTrace = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    request: TraceRequest
): Promise<ObjectTrace> => {
    const params = new URLSearchParams();

    if (request.id) {
        params.set('id', request.id);
    }

    if (request.objectType) {
        params.set('objectType', request.objectType);
    }

    if (request.relationId) {
        params.set('relationId', request.relationId);
    }

    if (request.from) {
        params.set('from', day(request.from));
    }

    if (request.to) {
        params.set('to', day(request.to));
    }

    if (request.stackTraces) {
        params.set('stackTraces', 'true');
    }

    const response = await axios.get(
        `/Trace/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}?${params.toString()}`
    );

    return parse<ObjectTrace>(response.data);
};

/**
 * One stored body, fetched because somebody opened it.
 *
 * A POST, and not because anything is written: a stored copy is keyed by a guid and an
 * inbound event by whatever id Chargebee put on it, and neither belongs in a URL that ends
 * up in a proxy log.
 */
export const getTraceData = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    reference: TraceDataRef
): Promise<TraceData> => {
    const response = await axios.post(
        `/Trace/${encodeURIComponent(customer)}/${encodeURIComponent(
            environment
        )}/data`,
        reference
    );

    return parse<TraceData>(response.data);
};

/**
 * What each kind of stored body is, in one line.
 *
 * The distinction is the one that matters when something went wrong: the inbound event is
 * what the other system sent, the payload is what the connector made of it, and an object
 * Exact refuses is almost always a disagreement between the two.
 */
export const DATA_KIND: Record<TraceDataKind, { label: string; hint: string }> =
    {
        InboundEvent: {
            label: 'zoals aangeleverd',
            hint: 'De ruwe webhook zoals Chargebee hem stuurde, voordat de connector er iets van gemaakt had.',
        },
        Payload: {
            label: 'zoals de connector het bewaart',
            hint: 'Het model dat de connector van de gebeurtenis heeft gemaakt. Dit is waar de verzendfunction mee werkt, dus dit is wat er naar Exact gaat.',
        },
    };

/**
 * What a source means on screen, and whether it is bad news.
 *
 * "Mislukt" and "gelogd" are kept apart on purpose. A row in the attempt log is a failed
 * attempt; a row in the general log is only something that was written down, and two of the
 * routes that end an object's journey write nothing else. Colouring them the same would put
 * a red dot next to the one line that says the object was already synchronised.
 */
export const SOURCE_LABEL: Record<
    TraceSource,
    { label: string; hint: string }
> = {
    QueueMessageLog: {
        label: 'poging',
        hint: 'Een mislukte poging: de handler is erop stukgelopen en heeft de fout weggeschreven.',
    },
    SyncLog: {
        label: 'stand',
        hint: 'De stand aan het eind van die dag, zoals de synclog hem bijhoudt.',
    },
    Log: {
        label: 'logregel',
        hint: 'Uit het algemene logboek. Dat is niet per se een fout: sommige routes schrijven alleen hier iets weg.',
    },
    Queue: {
        label: 'in queue',
        hint: 'Op dit moment gezien in een queue.',
    },
};

/** How a row was tied to the object, for the readers who need to weigh it. */
export const MATCH_LABEL: Record<TraceMatch, string> = {
    QueueMessage: 'gevonden op het queuebericht',
    AdditionalInfo1: 'gevonden op het klantnummer',
    AdditionalInfo2: 'gevonden op het factuurnummer',
    MessageText:
        'gevonden in de tekst van de melding - dat kan toeval zijn, controleer de regel zelf',
    SyncState: 'de stand van die dag',
    InboundEvent:
        'gevonden op het gebeurtenis-id van de webhook die nog onverwerkt klaarligt',
};

/**
 * What each route is called on screen, and what it looks at.
 *
 * The label says what the route is for and the `where` names the table it reads, which is the
 * pair a reader needs. "Koppeltabel" on its own was neither: it is not the name of anything in
 * the storage account, so it could not be looked up, and it did not say what a coupling is.
 */
export const ROUTE_LABEL: Record<TraceRoute, { label: string; where: string }> =
    {
        Relations: {
            label: 'Koppelingen Chargebee ↔ Exact',
            where: 'Tabel relations: per objectsoort het id waarmee de connector het object kent, met het Exact-id ernaast.',
        },
        ObjectPayload: {
            label: 'Vertaalde objecten',
            where: 'De tabellen objects, objectscb en objectsex, op ObjectGuid.',
        },
        ExactId: {
            label: 'Exact-id',
            where: 'Dezelfde tabel relations, maar dan gezocht op het id aan de Exact-kant.',
        },
        InboundEvent: {
            label: 'Ontvangen webhooks',
            where: 'De ruwe berichten die nog niet verwerkt zijn. Deze tabellen zijn niet op objectnummer gesleuteld, dus die worden doorlopen.',
        },
        LogColumns: {
            label: 'Logboeken op klant- en factuurnummer',
            where: 'De twee kolommen die de connector bij elk bericht meeschrijft. Exact, geen tekstzoektocht.',
        },
        LogText: {
            label: 'Logboeken op meldingtekst',
            where: 'De tekst van elke melding, per dag. Zwakker: een treffer kan toeval zijn.',
        },
    };

/** Why the zoektocht stopped waar hij stopte, in één regel voor de lezer. */
export const STOP_LABEL: Record<TraceSearchStop, string> = {
    Found: 'Er is iets gevonden, dus er is niet nog verder terug gezocht.',
    RangeExhausted:
        'Alle dagen van de gekozen periode zijn gelezen; geen van die dagen noemt dit id.',
    DayBudget:
        'De grens van wat één zoekopdracht mag lezen is bereikt. Oudere dagen bestaan mogelijk nog; die zijn alleen nog niet gelezen.',
    Failed: 'De logboeken konden niet (volledig) gelezen worden, dus "niets gevonden" zegt hier niets.',
    MentionsOnly:
        'Er zijn wel regels die dit id noemen, maar het object zelf is niet gevonden. Die regels komen van andere objecten - meestal betalingen die op deze factuur wachten. Er zijn nog oudere dagen te doorzoeken.',
};

/** How a related object was linked. */
export const VIA_LABEL: Record<TraceRelationVia, string> = {
    AdditionalInfo1: 'zelfde klant',
    AdditionalInfo2: 'wijst naar dit factuurnummer',
    MessageText: 'genoemd in de melding',
};

/** Waarom die koppeling er is, voor wie wil weten hoe hard hij is. */
export const VIA_HINT: Record<TraceRelationVia, string> = {
    AdditionalInfo1:
        'De handler schrijft het klantnummer bij elk bericht mee. Deze twee objecten horen dus bij dezelfde klant.',
    AdditionalInfo2:
        'De handler schrijft het factuurnummer bij elk bericht mee. Dit object wacht op die factuur - het factuurnummer staat niet in het queuebericht zelf, alleen in deze kolom.',
    MessageText:
        'Het id komt voor in de tekst van de melding. Dat is zwakker dan een kolom en kan toeval zijn.',
};

/**
 * What a station state means, and how firmly it is meant.
 *
 * Six states rather than the two a traffic light would give, because the two that carry the
 * panel are the ones a traffic light has no room for. "Geen spoor" is the honest answer
 * wherever a step writes nothing per object - which is most of the first half of the chain -
 * and "niet aan de orde" says a station plays no part for this object at all. Painting either
 * of them green or red would be a guess presented as a measurement.
 *
 * The label is deliberately about the evidence rather than about the object. "Niet vast te
 * stellen" was read as a failure of the panel, and on half the chain in a row it read as a
 * panel that knew nothing at all; "geen spoor" says the same thing about the tables, which is
 * what it is.
 */
export const STATION_STATE: Record<
    TraceStationState,
    { label: string; hint: string; tone: 'ok' | 'warn' | 'error' | 'muted' }
> = {
    Passed: {
        label: 'gepasseerd',
        hint: 'Het object is hier langs geweest.',
        tone: 'ok',
    },
    Here: {
        label: 'staat hier nu',
        hint: 'Op dit moment hier waargenomen.',
        tone: 'warn',
    },
    Failed: {
        label: 'hier gaat het mis',
        hint: 'Deze stap heeft het object niet kunnen verwerken.',
        tone: 'error',
    },
    Blocked: {
        label: 'komt niet verder',
        hint: 'Zolang hier niets verandert, heeft opnieuw proberen geen zin.',
        tone: 'error',
    },
    Unknown: {
        label: 'geen spoor',
        hint: 'Deze stap schrijft niets per object weg, dus er is geen spoor van dit object. Dat is niet hetzelfde als: het is er niet langs geweest.',
        tone: 'muted',
    },
    Skipped: {
        label: 'niet aan de orde',
        hint: 'Deze stap speelt voor dit object geen rol.',
        tone: 'muted',
    },
};

/**
 * What each kind of station is, in one word and in one line.
 *
 * The word is the point. A chain of ten steps that all look alike is a chain nobody can read
 * at a glance, and "wat is dit punt eigenlijk" was a question you could only answer by
 * hovering: a queue, a function, a table and an endpoint behave completely differently, and
 * whether it makes sense to press anything depends entirely on which of the four you are
 * looking at.
 */
export const STATION_KIND: Record<
    TraceStationKind,
    { label: string; hint: string }
> = {
    System: {
        label: 'systeem',
        hint: 'Systeem buiten de connector.',
    },
    Http: {
        label: 'endpoint',
        hint: 'Het endpoint waar het andere systeem op post.',
    },
    Queue: {
        label: 'queue',
        hint: 'Een wachtrij in de storage account. Berichten wachten hier tot een function ze oppakt.',
    },
    Worker: {
        label: 'function',
        hint: 'Een function die op die wachtrij luistert en de berichten verwerkt.',
    },
    Store: {
        label: 'tabel',
        hint: 'Een tabel waarin de connector het object bewaart.',
    },
    Poison: {
        label: 'poison',
        hint: 'De wachtrij voor berichten die het na alle pogingen niet gehaald hebben. Hier leest niemand uit.',
    },
};

/**
 * What each kind of input or output is, for the row that shows one.
 *
 * The label sits next to the one the API sends, not in place of it: the API says what this
 * particular thing is ("Queuebericht klaargezet"), and this says what sort of thing that is,
 * which is what the icon and the colour are keyed on.
 */
export const IO_KIND: Record<TraceIoKind, { label: string; hint: string }> = {
    QueueMessage: {
        label: 'queuebericht',
        hint: 'Een bericht op een wachtrij: een korte regel met komma-gescheiden velden, en het enige dat een function krijgt.',
    },
    Body: {
        label: 'opgeslagen data',
        hint: 'Een bewaarde body: de ruwe gebeurtenis, of de kopie die de connector ervan maakte.',
    },
    Row: {
        label: 'rij in een tabel',
        hint: 'Een rij die deze stap wegschrijft.',
    },
    HttpCall: {
        label: 'http-aanroep',
        hint: 'Een aanroep van buiten naar de connector.',
    },
    ApiCall: {
        label: 'api-aanroep',
        hint: 'Een aanroep van de connector naar het systeem waar het object heen moet.',
    },
};

/** How loud a log row is, so a warning does not read like an error. */
export const LEVEL_LABEL: Record<
    TraceLevel,
    { label: string; tone: 'error' | 'warn' | 'muted' }
> = {
    Error: { label: 'fout', tone: 'error' },
    Warning: { label: 'waarschuwing', tone: 'warn' },
    Information: { label: 'info', tone: 'muted' },
    Verbose: { label: 'detail', tone: 'muted' },
};

/** The steps of a trace, in the order they run, with what each one is doing. */
export const PHASE_LABEL: Record<TracePhase, string> = {
    Resolve: 'Object opzoeken',
    LogSearch: 'Logboeken doorzoeken',
    Window: 'Dagen bepalen',
    Timeline: 'Tijdlijn opbouwen',
    Context: 'Huidige stand ophalen',
    Done: 'Klaar',
};

/** The order the steps appear in, so a finished list keeps its shape. */
export const PHASE_ORDER: TracePhase[] = [
    'Resolve',
    'LogSearch',
    'Window',
    'Timeline',
    'Context',
];

/** Seconds, for the elapsed time next to a running step. */
export const formatElapsed = (ms: number): string =>
    ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
